import { isNil, sum } from "lodash-es";

import { createAppSelector, selectAssessments, selectCurrentCourseId, selectPages } from "./base";
import { selectPageGroupUsersForCurrentCourse } from "./page-groups";
import { isIndividualSubmission } from "./type-helpers";
import type { GradeCategory } from "./types";

import type {
  ActivityPageType,
  ActivityType,
  PageGroupUserType,
  PageType,
  SubmissionType,
} from "components/server-types";
import { isPrep } from "utils/activity";

export const selectCourseGradePages = createAppSelector(
  [selectPages, selectCurrentCourseId, (state, courseId: string | null) => courseId],
  (pages, currentCourseId, courseId) =>
    pages.filter(
      (page) =>
        page.course_id === (courseId ?? currentCourseId) &&
        page.max_points > 0 &&
        page.released_at &&
        page.assessments_published_at,
    ),
);

export const selectCourseGradePageCount = createAppSelector(
  [selectCourseGradePages],
  (pages) => pages.length,
);

const filterPagesByGradeCategory = (
  pages: PageType[],
  category: GradeCategory,
  activityMap: Record<string, ActivityType> = {},
): PageType[] => {
  if (category === "assignments") {
    return pages.filter((page) => page.category === "assignment");
  }

  const activityPages = pages.filter(
    (page) => page.category === "activity_page",
  ) as ActivityPageType[];

  if (category === "prepwork") {
    return activityPages.filter((page) => {
      const activity = activityMap[page.activity_id];
      return activity && isPrep(activity.category);
    });
  }
  return activityPages.filter((page) => {
    const activity = activityMap[page.activity_id];
    return activity && !isPrep(activity.category);
  });
};

const getCourseUserIdsForSubmission = (
  submission: SubmissionType,
  pageGroupUsers: PageGroupUserType[],
): string[] => {
  if (isIndividualSubmission(submission)) {
    return [submission.course_user_id];
  }
  const pageGroupUsersForSubmission = pageGroupUsers.filter(
    (pgu) => pgu.page_group_id === submission.page_group_id,
  );
  return pageGroupUsersForSubmission.map((pgu) => pgu.course_user_id);
};

/**
 * This will return a number between 0 and 1, representing the average grade for the given category.
 * If there are no pages in the category that qualify for grading, it will return undefined.
 */
export const selectAverageCourseGradeForGradeCategory = createAppSelector(
  [
    (state) => state.activities,
    (state) => state.submissions,
    (state) => state.pages,
    (state, category: GradeCategory) => category,
    (state, category: GradeCategory, courseUserId?: string | null) => courseUserId,
    selectPageGroupUsersForCurrentCourse,
    selectAssessments,
    (state) => selectCourseGradePages(state, null), // Need to specify null or category will be passed as second arg
  ],
  (
    activityMap,
    submissionsMap,
    pageMap,
    category,
    courseUserId,
    pageGroupUsers,
    assessments,
    pages,
  ) => {
    const pagesForCategory = filterPagesByGradeCategory(pages, category, activityMap);
    if (pagesForCategory.length === 0) {
      return undefined;
    }
    const pageIds = new Set(pagesForCategory.map((p) => p.id));

    // We define this `matchCourseUserId` function to filter submissions based on the course user ID,
    // if one is provided. If not, it will match all submissions.
    const matchCourseUserId = courseUserId
      ? (submission: SubmissionType) =>
          getCourseUserIdsForSubmission(submission, pageGroupUsers).includes(courseUserId)
      : () => true;

    const submissionsForCategory = Object.values(submissionsMap).filter(
      (submission) =>
        submission.official_at && matchCourseUserId(submission) && pageIds.has(submission.page_id),
    );
    const submissionIds = new Set(submissionsForCategory.map((s) => s.id));

    const assessmentsForCategory = assessments.filter((assessment) =>
      submissionIds.has(assessment.submission_id),
    );

    // Time to calculate! Note that we ONLY calculate based on assessments.
    // If the student does not have a submission for a page, or the submission is not assessed,
    // then we ignore that page for the average.

    // For an individual student, this is pretty straightforward:
    if (courseUserId) {
      const assessedPoints = sum(assessmentsForCategory.map((a) => a.points));
      const maxPoints = sum(
        assessmentsForCategory.map((assessment) => {
          const submission = submissionsMap[assessment.submission_id];
          const page = pageMap[submission.page_id];
          return isNil(assessment.points) ? 0 : page.max_points;
        }),
      );
      return maxPoints > 0 ? assessedPoints / maxPoints : undefined;
    }

    // For the entire class, we need to handle group submissions more carefully.
    // Group submissions need to be counted multiple times: once for each student in the group.
    // We can use the `getCourseUserIdsForSubmission` function to determine how many times
    // to count each submission; it will return an array of length 1 for individual submissions.
    const [assessedPoints, maxPoints] = assessmentsForCategory.reduce(
      ([accumulatedPoints, accumulatedMaxPoints], assessment) => {
        const submission = submissionsMap[assessment.submission_id];
        const page = pageMap[submission.page_id];
        const courseUserIds = getCourseUserIdsForSubmission(submission, pageGroupUsers);
        return [
          accumulatedPoints + (assessment.points || 0) * courseUserIds.length,
          accumulatedMaxPoints + page.max_points * courseUserIds.length,
        ];
      },
      [0, 0] as [number, number],
    );
    return maxPoints > 0 ? assessedPoints / maxPoints : undefined;
  },
);
